import React, { createContext, useContext, useState, useEffect } from "react";
import { ENV } from "../constants/shared";
import type {
  ActiveStep,
  ProcessedIntegration,
  CarbonConnectProps,
  OnSuccessData,
} from "../typing/shared";
import { IntegrationName, EmbeddingGenerators } from "../typing/shared";
import { getBaseURL } from "../utils/helper-functions";
import { INTEGRATIONS_LIST } from "../utils/integrationModalconstants";
import log from "loglevel";
import captureException from "instrumentation/sendFaroErrors";

type CarbonContextValues = CarbonConnectProps & {
  accessToken?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authenticatedFetch?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchTokens?: any;
  processedIntegrations?: ProcessedIntegration[];
  processedFCIntegrations?: ProcessedIntegration[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRequestIds?: any;
  requestIds?: object;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  whiteLabelingData?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userData?: any;
  entryPointIntegrationObject?: ProcessedIntegration | null;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  manageModalOpenState?: any;
  activeStep?: ActiveStep;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setActiveStep?: any;
  showModal?: boolean;

  slackActive?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSlackActive?: any;

  lastModifications?: OnSuccessData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setLastModifications?: any;
};

const CarbonContext: React.Context<CarbonContextValues> = createContext({
  orgName: "",
  brandIcon: "",
});

export const CarbonProvider = ({
  alwaysOpen = false,
  apiURL = null,
  backButtonText = "Go Back",
  brandIcon,
  children,
  chunkSize = 1500,
  dataSourcePollingInterval,
  dataSourceTags = {},
  dataSourceTagsFilterQuery = {},
  embeddingModel = EmbeddingGenerators.OPENAI,
  enabledFCIntegrations = [],
  enabledIntegrations = [
    {
      id: IntegrationName.LOCAL_FILES,
      chunkSize: 100,
      overlapSize: 10,
      maxFileSize: 20000000,
      skipEmbeddingGeneration: false,
      setPageAsBoundary: false,
      sendDeletionWebhooks: false,
      allowedFileTypes: [
        {
          extension: "csv",
        },
        {
          extension: "txt",
        },
        {
          extension: "pdf",
        },
      ],
    },
  ],
  entryPoint,
  environment = ENV.PRODUCTION,
  filesTabColumns,
  fileSyncConfig = {},
  generateSparseVectors = false,
  incrementalSync = false,
  installingIntegrationId = null,
  maxFileSize = 20000000,
  maxItemsPerChunk,
  navigateBackURL = null,
  onError = () => {},
  onSuccess = () => {},
  open = false,
  openFilesTabTo = "FILES_LIST",
  orgName,
  overlapSize = 20,
  parsePdfTablesWithOcr = false,
  prependFilenameToChunks = false,
  privacyPolicyURL = "https://carbon.ai/privacy",
  sendDeletionWebhooks = false,
  setOpen = null,
  setPageAsBoundary = false,
  showFilesTab = true,
  tags = {},
  tokenFetcher,
  tosURL = "https://carbon.ai/terms",
  useOcr = false,
  useRequestIds = false,
  zIndex = 1000,
}: CarbonConnectProps) => {
  const [showModal, setShowModal] = useState(open);
  const [loading, setLoading] = useState(false);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [processedIntegrations, setProcessedIntegrations] = useState<
    ProcessedIntegration[]
  >([]);
  const [processedFCIntegrations, setProcessedFCIntegrations] = useState<
    ProcessedIntegration[]
  >([]);
  const [entryPointIntegrationObject, setEntryPointIntegrationObject] =
    useState<ProcessedIntegration | null>(null);
  const [whiteLabelingData, setWhiteLabelingData] = useState(null);
  const [requestIds, setRequestIds] = useState({});
  const [userData, setUserData] = useState(null);

  const [slackActive, setSlackActive] = useState(false);

  const [lastModifications, setLastModifications] = useState([]);

  const manageModalOpenState = (modalOpenState: boolean) => {
    if (alwaysOpen) return;

    if (setOpen) setOpen(modalOpenState);

    setShowModal(modalOpenState);
  };

  const authenticatedFetch = async (
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: any = {},
    retry = true,
  ): Promise<Response | undefined> => {
    try {
      const response = await fetch(url, {
        body: options.body,
        method: options.method,
        headers: options.headers,
      });

      if (response.status === 401 && retry && tokenFetcher) {
        const response = await tokenFetcher();

        setAccessToken(response?.access_token || null);

        const newOptions = {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Token ${response.access_token}`,
          },
        };

        return await authenticatedFetch(url, newOptions, false); // Passing 'false' to avoid endless loop in case refreshing the token doesn't help
      }

      return response;
    } catch (err) {
      captureException(err);
      log.error(
        `[CarbonContext.js] Error in authenticatedFetch [${url}]: `,
        err,
      );
    }
  };

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const response = tokenFetcher && (await tokenFetcher());

      setAccessToken(response?.access_token || null);

      const whiteLabelingResponse = await authenticatedFetch(
        `${getBaseURL(apiURL, environment)}/auth/v1/white_labeling`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${response?.access_token || null}`,
          },
        },
      );
      const whiteLabelingResponseData = await whiteLabelingResponse?.json();

      setWhiteLabelingData(whiteLabelingResponseData);
      const userResponse = await authenticatedFetch(
        `${getBaseURL(apiURL, environment)}/whoami`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${response?.access_token || null}`,
          },
        },
      );

      if (userResponse?.status == 200) {
        setUserData(await userResponse.json());
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[CarbonContext.js] Error in fetchTokens: ", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const tempIntegrations = [];
    const tempFCIntegrations = [];

    // merge the integration data like logos etc with the options provided as props
    for (let i = 0; i < enabledIntegrations.length; i++) {
      const integrationOptions = enabledIntegrations[i];
      const integration = INTEGRATIONS_LIST?.find(
        (integration) =>
          integrationOptions.id === integration.id && integration.active,
      );

      if (!integration) continue;

      tempIntegrations.push({ ...integrationOptions, ...integration });
    }

    for (let i = 0; i < enabledFCIntegrations.length; i++) {
      const integrationOptions = enabledFCIntegrations[i];
      const integration = INTEGRATIONS_LIST?.find(
        (integration) =>
          integrationOptions.id === integration.id && integration.active,
      );

      if (!integration) continue;

      tempFCIntegrations.push({ ...integrationOptions, ...integration });
    }

    setProcessedIntegrations(tempIntegrations);
    setProcessedFCIntegrations(tempFCIntegrations);

    if (entryPoint && entryPoint !== "INTEGRATION_LIST") {
      const obj = tempIntegrations.find(
        (integration) => integration.id === entryPoint,
      );

      if (!obj) {
        const isIntegrationAvailable = INTEGRATIONS_LIST.find(
          (integration) => integration.id === entryPoint,
        );

        if (isIntegrationAvailable) {
          // eslint-disable-next-line no-console
          console.error(
            "Invalid entry point. Make sure that the integrations is enabled through enabledIntegrations prop.",
          );
        } else {
          // eslint-disable-next-line no-console
          console.error(
            "Invalid entry point. Make sure that right integration id is passed.",
          );
        }
      }

      setEntryPointIntegrationObject(obj || null);
    }
  }, []);

  useEffect(() => {
    setShowModal(open);
  }, [open]);

  const contextValues = {
    accessToken,
    setAccessToken,
    fetchTokens,
    authenticatedFetch,
    enabledIntegrations,
    orgName,
    brandIcon,
    environment,
    entryPoint,
    tags,
    maxFileSize,
    onSuccess,
    onError,
    chunkSize,
    overlapSize,
    processedIntegrations,
    entryPointIntegrationObject,
    whiteLabelingData,
    tosURL,
    privacyPolicyURL,
    open,
    setOpen,
    showModal,
    setShowModal,
    alwaysOpen,
    navigateBackURL,
    manageModalOpenState,
    backButtonText,
    zIndex,
    embeddingModel,
    generateSparseVectors,
    prependFilenameToChunks,
    maxItemsPerChunk,
    setPageAsBoundary,
    setRequestIds,
    requestIds,
    useRequestIds,
    useOcr,
    parsePdfTablesWithOcr,
    loading,
    sendDeletionWebhooks,
    fileSyncConfig,
    slackActive,
    setSlackActive,
    filesTabColumns,
    incrementalSync,
    showFilesTab,
    dataSourcePollingInterval,
    lastModifications,
    setLastModifications,
    openFilesTabTo,
    apiURL,
    userData,
    dataSourceTags,
    dataSourceTagsFilterQuery,
    installingIntegrationId,
    processedFCIntegrations,
  };

  return (
    <CarbonContext.Provider value={contextValues}>
      {children}
    </CarbonContext.Provider>
  );
};

export const useCarbon = () => {
  const context = useContext(CarbonContext);

  if (context === undefined) {
    throw new Error("useCarbon must be used within CarbonProvider");
  }

  return context;
};

export default CarbonContext;
